import { isTimePermissionEnabled } from '../config/applicationFeatures';
/**
 * Request-type / entity-type codes observed in workflow contracts.
 * Leave workflow entity is LEAVE_APPLICATION (module APIs); LEAVE_REQUEST kept for legacy.
 */
const LABELS: Record<string, string> = {
  LEAVE_APPLICATION: 'Leave request',
  LEAVE_REQUEST: 'Leave request',
  LEAVE: 'Leave request',
  WFH_REQUEST: 'Work from home request',
  WFH: 'Work from home request',
  PERMISSION_REQUEST: 'Permission request',
  PERMISSION: 'Permission request',
  ATTENDANCE_CORRECTION: 'Attendance correction',
};

export function getWorkflowRequestTypeLabel(
  code: string | null | undefined,
): string {
  if (!code) return 'Request';
  return LABELS[code.toUpperCase()] ?? 'Request';
}

export function getWorkflowRequestTypeSummary(
  code: string | null | undefined,
): string {
  const label = getWorkflowRequestTypeLabel(code);
  if (label === 'Request') {
    return 'A workflow request is waiting for review.';
  }
  return `A ${label.toLowerCase()} is waiting for review.`;
}

/**
 * Returns a known app route name for linked business details, or null.
 * Callers must still check permissions and a valid reference ID.
 */
export function getWorkflowRequestRoute(
  code: string | null | undefined,
):
  | 'leaveDetails'
  | 'wfhDetails'
  | 'timePermissionDetails'
  | 'attendanceCorrectionDetails'
  | null {
  if (!code) return null;
  const normalized = code.toUpperCase();
  if (
    normalized === 'LEAVE_APPLICATION' ||
    normalized === 'LEAVE_REQUEST' ||
    normalized === 'LEAVE'
  ) {
    return 'leaveDetails';
  }
  if (normalized === 'WFH_REQUEST' || normalized === 'WFH') {
    return 'wfhDetails';
  }
  if (normalized === 'PERMISSION_REQUEST' || normalized === 'PERMISSION') {
    return isTimePermissionEnabled() ? 'timePermissionDetails' : null;
  }
  if (normalized === 'ATTENDANCE_CORRECTION') {
    return 'attendanceCorrectionDetails';
  }
  return null;
}

/**
 * Module approval screens for leave / WFH / permission workflows.
 * Generic workflow approve/reject APIs are disabled; these screens call
 * the mounted module approve/reject endpoints instead.
 */
export function getWorkflowApprovalRoute(
  code: string | null | undefined,
):
  | 'leaveApproval'
  | 'wfhApproval'
  | 'timePermissionApproval'
  | 'attendanceCorrectionApproval'
  | null {
  if (!code) return null;
  const normalized = code.toUpperCase();
  if (
    normalized === 'LEAVE_APPLICATION' ||
    normalized === 'LEAVE_REQUEST' ||
    normalized === 'LEAVE'
  ) {
    return 'leaveApproval';
  }
  if (normalized === 'WFH_REQUEST' || normalized === 'WFH') {
    return 'wfhApproval';
  }
  if (normalized === 'PERMISSION_REQUEST' || normalized === 'PERMISSION') {
    return isTimePermissionEnabled() ? 'timePermissionApproval' : null;
  }
  if (normalized === 'ATTENDANCE_CORRECTION') {
    return 'attendanceCorrectionApproval';
  }
  return null;
}
